import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type Report } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PdfViewer } from '../components/PdfViewer';
import { AccessRequestForm } from '../components/AccessRequestForm';

export function ReportsPage() {
  const { canDownload, accessMode, requestStatus, reviewerNotes, reviewedAt, notifyNewDocuments, pendingReRequest, pendingRequestDetails, isAdmin, refreshMe } = useAuth();
  const { t } = useTranslation();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReports, setSelectedReports] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState(false);
  const [viewingReport, setViewingReport] = useState<Report | null>(null);
  const isFirstTime = !requestStatus && !isAdmin;
  const [showRequestForm, setShowRequestForm] = useState(false);

  const approved = requestStatus === 'APPROVED';
  const canAccess = canDownload || accessMode === 'view' || approved;
  const isViewOnly = accessMode === 'view' && !canDownload && approved;

  const dismissalKey = useMemo(
    () => (reviewedAt ? `pci_portal_decision_seen:${reviewedAt}` : null),
    [reviewedAt],
  );
  const [decisionDismissed, setDecisionDismissed] = useState<boolean>(() => {
    if (!dismissalKey) return false;
    return localStorage.getItem(dismissalKey) === '1';
  });

  useEffect(() => {
    if (dismissalKey && !decisionDismissed && requestStatus && (requestStatus === 'APPROVED' || requestStatus === 'DENIED')) {
      localStorage.setItem(dismissalKey, '1');
    }
  }, [dismissalKey, decisionDismissed, requestStatus]);

  function dismissDecision() {
    if (dismissalKey) localStorage.setItem(dismissalKey, '1');
    setDecisionDismissed(true);
  }

  useEffect(() => {
    loadReports();
  }, []);

  async function loadReports() {
    try {
      const data = await api.listReports();
      setReports(data.reports);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }

  const groupedReports = useMemo(() => {
    const typeOrder = ['PCI', 'SOC', 'ISO', 'Other', 'ROC'];
    const groups: { type: string; reports: Report[] }[] = [];
    for (const type of typeOrder) {
      const matching = reports
        .filter((r) => r.docType === type)
        .sort((a, b) => (b.year || 0) - (a.year || 0));
      if (matching.length > 0) {
        groups.push({ type, reports: matching });
      }
    }
    return groups;
  }, [reports]);

  function toggleReport(reportId: string) {
    setSelectedReports((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) {
        next.delete(reportId);
      } else {
        next.add(reportId);
      }
      return next;
    });
  }

  function toggleAll() {
    if (selectedReports.size === reports.length) {
      setSelectedReports(new Set());
    } else {
      setSelectedReports(new Set(reports.map((r) => r.id)));
    }
  }

  async function handleDownload() {
    if (selectedReports.size === 0) return;
    setDownloading(true);
    setError('');
    try {
      for (const reportId of selectedReports) {
        const data = await api.downloadReport(reportId);
        const link = document.createElement('a');
        link.href = data.url;
        link.download = '';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
        if (selectedReports.size > 1) {
          await new Promise((r) => setTimeout(r, 500));
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  }

  function handleView(report: Report) {
    setViewingReport(report);
  }

  if (loading) return <div className="loading">{t('reports.loadingReports')}</div>;

  return (
    <div className="reports-page">
      <h2>{t('reports.title')}</h2>
      <p className="page-description">
        {t('reports.description')}
      </p>

      {isViewOnly && (
        <div className="alert alert-info">
          <strong>{t('reports.viewOnlyNotice', 'View Only Access')}</strong>{' '}
          {t('reports.viewOnlyDetail', 'You have view-only access to documents. Downloading is not available.')}
        </div>
      )}

      {requestStatus === 'APPROVED' && !decisionDismissed && !isViewOnly && (
        <div className="alert alert-success" role="status">
          <strong>{t('reports.approved')}</strong> {t('reports.approvedDetail')}{' '}
          <button type="button" className="alert-dismiss" onClick={dismissDecision} aria-label={t('common.dismiss')}>×</button>
        </div>
      )}

      {requestStatus === 'DENIED' && !decisionDismissed && (
        <div className="alert alert-error" role="alert">
          <strong>{t('reports.denied')}</strong>
          {reviewerNotes ? <> {t('reports.deniedNotes', { notes: reviewerNotes })}</> : null}{' '}
          {t('common.questionsContact')} <a href="mailto:pci-contact@amazon.com">pci-contact@amazon.com</a>.
          <button type="button" className="alert-dismiss" onClick={dismissDecision} aria-label={t('common.dismiss')}>×</button>
        </div>
      )}

      {(pendingReRequest || (!canAccess && requestStatus !== 'DENIED')) && (
        <div className="alert alert-info">
          <strong>{t('reports.pending')}</strong>{' '}
          {pendingRequestDetails && pendingRequestDetails.requestedDocuments.length > 0 ? (
            <>
              Your {pendingRequestDetails.requestedDocuments.some((d) => d.accessMode === 'download') && pendingRequestDetails.requestedDocuments.some((d) => d.accessMode === 'view')
                ? 'view/download'
                : pendingRequestDetails.requestedDocuments.every((d) => d.accessMode === 'view')
                  ? 'view'
                  : 'download'} request is awaiting approval for:{' '}
              <ul className="pending-docs-list">
                {pendingRequestDetails.requestedDocuments.map((doc) => (
                  <li key={doc.reportId}>{doc.reportId} ({doc.accessMode})</li>
                ))}
              </ul>
            </>
          ) : (
            <>{t('reports.pendingDetail')}{' '}</>
          )}
          {t('common.questionsContact')} <a href="mailto:pci-contact@amazon.com">pci-contact@amazon.com</a>.
        </div>
      )}

      {/* Request access update button — visible to non-admin users */}
      {requestStatus && (
        <div className="request-update-section">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowRequestForm(true)}
          >
            {requestStatus === 'DENIED'
              ? t('reports.reRequest', 'Re-request Access')
              : t('reports.updateAccess', 'Request Access Update')}
          </button>
        </div>
      )}

      {(showRequestForm || isFirstTime) && (
        <AccessRequestForm
          onClose={() => setShowRequestForm(false)}
          onSubmitted={() => {
            setShowRequestForm(false);
            window.location.reload();
          }}
        />
      )}

      {!isAdmin && (
        <div className="notification-pref">
          <label className="aoc-check-row">
            <input
              type="checkbox"
              checked={notifyNewDocuments}
              onChange={async (e) => {
                const val = e.target.checked;
                await api.updateNotifications(val);
                refreshMe();
              }}
            />
            <span className="aoc-check-label">
              {t('reports.notifyPref', 'Notify me when new compliance documents are available')}
            </span>
          </label>
        </div>
      )}

      {error && <div className="alert alert-error" role="alert">{error}</div>}

      {reports.length === 0 ? (
        <div className="empty-state">
          <p>
            {t('reports.empty')}{' '}
            <a href="mailto:pci-contact@amazon.com">pci-contact@amazon.com</a>.
          </p>
        </div>
      ) : (
        <>
          {!isViewOnly && (
            <div className="reports-select-all">
              <label className="select-all-label">
                <input
                  type="checkbox"
                  checked={selectedReports.size === reports.length}
                  onChange={toggleAll}
                  className="report-checkbox"
                />
                {t('reports.selectAll')}
              </label>
            </div>
          )}

          {groupedReports.map((group) => (
            <div key={group.type} className="reports-type-section">
              <h3 className="reports-type-heading">
                <span className={`badge badge-${group.type === 'ROC' ? 'warn' : (group.type || 'other').toLowerCase()} report-doc-badge`}>{group.type}</span>
                {group.type === 'PCI' && ' PCI DSS Documents'}
                {group.type === 'SOC' && ' SOC Reports'}
                {group.type === 'ISO' && ' ISO Certificates'}
                {group.type === 'Other' && ' Other Documents'}
                {group.type === 'ROC' && ' Reports on Compliance'}
              </h3>
              <div className="reports-list" role="group" aria-label={`${group.type} reports`}>
                {group.reports.map((report) => (
                  <label
                    key={report.id}
                    className={`report-card ${selectedReports.has(report.id) ? 'selected' : ''}`}
                  >
                    {!isViewOnly && (
                      <input
                        type="checkbox"
                        value={report.id}
                        checked={selectedReports.has(report.id)}
                        onChange={() => toggleReport(report.id)}
                        className="report-checkbox"
                      />
                    )}
                    <div className="report-name-cell">
                      <div className="report-name">
                        {report.friendlyName || report.id}
                        {report.year && (
                          <span className="badge badge-pending report-doc-badge">{report.year}</span>
                        )}
                      </div>
                      <div className="report-meta">
                        <span className="report-date">
                          {t('reports.reportDate')}{' '}
                          {report.reportDate
                            ? new Date(report.reportDate.slice(0, 10) + 'T00:00:00').toLocaleDateString()
                            : new Date(report.lastModified).toLocaleDateString()}
                        </span>
                        {report.uploadedBy && (
                          <span className="report-uploader">{t('reports.uploadedBy')} {report.uploadedBy}</span>
                        )}
                      </div>
                    </div>
                    <div className="report-description-cell">
                      {report.description || ''}
                    </div>
                    {isViewOnly && (
                      <button
                        type="button"
                        className="btn-primary btn-view-report"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleView(report);
                        }}
                      >
                        {t('reports.viewButton', 'View')}
                      </button>
                    )}
                  </label>
                ))}
              </div>
            </div>
          ))}

          {!isViewOnly && (
            <button
              onClick={handleDownload}
              disabled={selectedReports.size === 0 || downloading || !canAccess}
              className="btn-download"
            >
              {downloading
                ? t('reports.downloading')
                : !canAccess
                  ? t('reports.downloadPending')
                  : selectedReports.size > 1
                    ? t('reports.downloadSelected', { count: selectedReports.size })
                    : t('reports.downloadButton')}
            </button>
          )}
        </>
      )}

      {viewingReport && (
        <PdfViewer
          reportId={viewingReport.id}
          reportName={viewingReport.friendlyName || viewingReport.id}
          onClose={() => setViewingReport(null)}
        />
      )}
    </div>
  );
}
